import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import { MergerSetupPanel } from "../../../../../mergers/mergerSetup";
import dayjs from "dayjs";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete
} from "@mui/material";
import {
  CommentDto,
  Broker,
  CreditRiskLifecycle
} from "../../../../../../api/Api";
import {
  useBrokerAssignees,
  useBrokerProgressOverride,
  useUpdateBroker
} from "../../../../dataHooks/brokerQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { WarehouseSyncDialog } from "../../../../../../components/lifecycle/warehouseSync";
import { HubSyncValues } from "../../../../../../components/lifecycle/warehouseSync/utils";
import {
  creditRiskChanges,
  mirroredSanctionScreening,
  patchTouchesCreditRiskData,
  syncedCreditRiskProgress
} from "../../../../../../components/lifecycle/warehouseSync/applyPatch";
import { brokerAttachmentApi } from "../../../brokerAttachmentApi";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificBroker: Broker;
};

type FormData = {
  kycCheck: string;
  kycCheckAssignee: string;
  sanctionScreening: string;
  kycCheckMft: string;
  kycCheckMftAssignee: string;
  creditCheck: string;
  creditCheckAssignee: string;
  creditAmount: number | undefined;
  creditCurrency: string;
  remitReporting: string;
  remitReportingAssignee: string;
  reportingSetup: string;
  comments: CommentDto[];
  signOffNote: string;
};

export const Compliance: FC<Props> = ({ specificBroker }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("broker");
  const roles = useRoles();
  const progressOverride = useBrokerProgressOverride(
    specificBroker,
    "compliance"
  );

  const [formData, setFormData] = useState<FormData>({
    kycCheck: specificBroker?.complianceLifecycle?.kycCheck || "",
    kycCheckAssignee:
      specificBroker?.complianceLifecycle?.kycCheckAssignee || "",
    sanctionScreening:
      specificBroker?.complianceLifecycle?.sanctionScreening || "",
    kycCheckMft: specificBroker?.complianceLifecycle?.kycCheckMft || "",
    kycCheckMftAssignee:
      specificBroker?.complianceLifecycle?.kycCheckMftAssignee || "",
    creditCheck: specificBroker?.creditRiskLifecycle?.creditCheck || "",
    creditCheckAssignee:
      specificBroker?.creditRiskLifecycle?.creditCheckAssignee || "",
    creditAmount:
      specificBroker?.creditRiskLifecycle?.creditAmount ?? undefined,
    creditCurrency: specificBroker?.creditRiskLifecycle?.creditCurrency || "",
    // ADHO-453: the gate moved from broker MA ("Reporting requirements") to
    // Compliance. Fall back to the old MA value so brokers answered before the
    // move keep their answer; it migrates on the next Compliance save.
    remitReporting:
      specificBroker?.complianceLifecycle?.remitReporting ||
      specificBroker?.marketAccessLifecycle?.reportingRequirements ||
      "",
    remitReportingAssignee:
      specificBroker?.complianceLifecycle?.remitReportingAssignee || "",
    reportingSetup: specificBroker?.complianceLifecycle?.reportingSetup || "",
    comments: specificBroker?.complianceLifecycle?.comments || [],
    signOffNote: specificBroker?.complianceLifecycle?.signOffNote || ""
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const updateBroker = useUpdateBroker({ onSuccess: handleClose });
  const commentUpdate = useUpdateBroker();

  const complianceAssignees = useBrokerAssignees("legal");

  // ADHO-490. The warehouse link lives on the broker root rather than inside a lifecycle, because
  // Compliance and Risk both read it. Accepting a sync only stages the values here; they are
  // written on Save or Complete like every other field in this dialog.
  const [syncOpen, setSyncOpen] = useState(false);
  const [catalystId, setCatalystId] = useState(
    specificBroker?.creditCatalystId || ""
  );
  const [syncedAt, setSyncedAt] = useState(
    specificBroker?.creditCatalystSyncedAt || ""
  );
  // The credit values a sync filled in, staged until Save or Complete.
  const [syncedCredit, setSyncedCredit] = useState<
    Partial<CreditRiskLifecycle>
  >({});
  const creditRiskDataSynced = Object.keys(syncedCredit).length > 0;

  const hubSyncValues: HubSyncValues = {
    kycCheck: formData.kycCheck,
    creditCheck: formData.creditCheck,
    creditAmount: formData.creditAmount,
    creditCurrency: formData.creditCurrency
  };

  // Approved with no amount means no cap, which is how Credit Risk records an unlimited broker and
  // how the warehouse stores it (M_CreditLimit NULL).
  const creditIsUnlimited =
    formData.creditCheck === "approved" && formData.creditAmount === undefined;

  // Keep the credit risk fields out of the compliance slice, so a save does not leave a dead copy
  // there that disagrees with the value Credit Risk owns.
  const complianceFormFields = () => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditCheck,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditCheckAssignee,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditAmount,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditCurrency,
      ...fields
    } = formData;
    return fields;
  };

  const handleApplySync = (patch: Partial<HubSyncValues>) => {
    setFormData((prev) => ({
      ...prev,
      ...patch,
      ...mirroredSanctionScreening(patch)
    }));
    if (patchTouchesCreditRiskData(patch)) {
      setSyncedCredit((prev) => ({ ...prev, ...creditRiskChanges(patch) }));
    }
    setSyncedAt(new Date().toISOString());
    setSyncOpen(false);
  };

  // A sync that fills in Credit Risk's data moves Credit Risk's progress with it. Only progress,
  // never creditRiskLifecycle.complete. See syncedCreditRiskProgress.
  const syncedCreditRiskProgressFields = creditRiskDataSynced
    ? { creditRisk: syncedCreditRiskProgress() }
    : {};

  const buildBroker = (
    values: ReturnType<typeof complianceFormFields> & { complete?: boolean }
  ): Broker => ({
    ...specificBroker,
    creditCatalystId: catalystId || undefined,
    creditCatalystSyncedAt: syncedAt || undefined,

    progress: {
      ...specificBroker.progress,
      ...syncedCreditRiskProgressFields
    },

    // The credit values are stored on creditRiskLifecycle, not on the compliance slice: this dialog
    // only mirrors them as disabled fields. Synced values therefore have to be written onto the
    // credit risk slice, or they land in complianceLifecycle where nothing reads them.
    //
    // Only the values a sync actually changed. Saving the values this dialog loaded would revert
    // whatever Credit Risk set while the dialog sat open, on fields Compliance cannot edit.
    ...(creditRiskDataSynced
      ? {
          creditRiskLifecycle: {
            ...specificBroker.creditRiskLifecycle,
            ...syncedCredit
          }
        }
      : {}),

    complianceLifecycle: {
      // Spread the stored lifecycle first: this dialog's formData does not cover
      // every compliance field, and rebuilding from formData alone silently
      // drops the ones it misses.
      ...specificBroker.complianceLifecycle,
      ...values
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildBroker({ ...complianceFormFields(), comments })
  });

  const handleChange = (
    field: keyof FormData,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData: complianceFormFields(),
    currentComment,
    currentProgress: specificBroker.progress?.compliance,
    canSetInProgress: ownsLifecycle(roles, "compliance"),
    buildEntity: (values, progress) => {
      const saved = buildBroker(values);
      // Preserve Credit Risk progress staged by the warehouse sync.
      return {
        ...saved,
        progress: { ...saved.progress, compliance: progress }
      };
    },
    mutate: updateBroker.mutate
  });

  const open = location.pathname.includes("compliance");

  const canComplete =
    formData.kycCheck &&
    (specificBroker?.marketAccessLifecycle?.kycCheckOnMft === "needed"
      ? formData.kycCheckMft
      : true);

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Compliance"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={brokerAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateBroker.isPending
      }}
    >
      <Box
        sx={{
          paddingTop: 2,
          gap: 2,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {specificBroker.isMerger && specificBroker.id && (
          <MergerSetupPanel
            kind="broker"
            entityId={specificBroker.id}
            mergerDescription={specificBroker.mergerDescription}
            answers={specificBroker.marketAccessLifecycle}
          />
        )}
        <Box display="flex" gap={2} alignItems="flex-start">
          <TextField
            sx={{ flex: 1 }}
            size="medium"
            label="Catalyst ID / BvD id"
            value={catalystId}
            onChange={(e) => setCatalystId(e.target.value)}
            helperText={
              syncedAt
                ? `Last synced ${dayjs(syncedAt).format("LL")}`
                : "Compliance calls it BvD id, Risk calls it Catalyst ID."
            }
          />
          <Button
            variant="outlined"
            onClick={() => setSyncOpen(true)}
            disabled={!catalystId.trim()}
            sx={{ marginTop: 1 }}
          >
            Sync with database
          </Button>
        </Box>

        <Box display="flex" gap={2}>
          <Box display="flex" flexDirection="column" gap={2} sx={{ flex: 1 }}>
            <FormControl size="medium" required>
              <InputLabel>KYC check</InputLabel>
              <Select
                label="KYC check"
                value={formData.kycCheck}
                onChange={(e) => {
                  handleChange("kycCheck", e.target.value);
                  handleChange("sanctionScreening", e.target.value);
                }}
              >
                <MenuItem value="initiated">Initiated</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Autocomplete
            sx={{ flex: 1, alignSelf: "center" }}
            options={complianceAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              complianceAssignees.data?.find(
                (a) => a.email === formData.kycCheckAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("kycCheckAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="KYC check + sanction screening assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {specificBroker.marketAccessLifecycle?.kycCheckOnMft === "needed" ? (
          <Box display="flex" gap={2}>
            <FormControl size="medium" required sx={{ flex: 1 }}>
              <InputLabel>KYC check on MFT</InputLabel>
              <Select
                label="KYC check on MFT"
                value={formData.kycCheckMft}
                onChange={(e) => handleChange("kycCheckMft", e.target.value)}
              >
                <MenuItem value="initiated">Initiated</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ flex: 1 }}
              options={complianceAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                complianceAssignees.data?.find(
                  (a) => a.email === formData.kycCheckMftAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange("kycCheckMftAssignee", newValue?.email || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="KYC check on MFT assignee"
                  size="medium"
                />
              )}
            />
          </Box>
        ) : null}

        <Box display="flex" gap={2}>
          <FormControl disabled size="medium" required sx={{ flex: 1 }}>
            <InputLabel>
              Credit check - to be completed by Credit Risk
            </InputLabel>
            <Select
              label="Credit check - to be completed by Credit Risk"
              value={formData.creditCheck}
              onChange={(e) => handleChange("creditCheck", e.target.value)}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            disabled
            sx={{ flex: 1 }}
            options={[]}
            getOptionLabel={(option) => `${option.email}`}
            value={{ email: formData.creditCheckAssignee, name: "" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Credit check assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {/* Only shown once credit is approved: a rejected broker has no amount
            worth displaying.

            An approved broker showing 0 is expected, not a bug: brokers carry no
            real credit limit, so the warehouse often holds M_CreditLimit 0, the
            sync derives approved from the assessment rather than the number
            (deriveCreditCheck), and the amount is copied across verbatim so the
            Hub matches the warehouse 1:1. */}
        {formData.creditCheck === "approved" && (
          <Box display="flex" gap={2}>
            <TextField
              disabled
              sx={{ flex: 1 }}
              label="Credit amount - filled by Credit Risk"
              // No amount on an approved credit check means no limit. A number
              // input cannot render that, so switch to text and say so.
              type={creditIsUnlimited ? "text" : "number"}
              value={
                creditIsUnlimited ? "Unlimited" : (formData.creditAmount ?? "")
              }
              slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
            />
            <TextField
              disabled
              sx={{ flex: 1 }}
              label="Credit currency - filled by Credit Risk"
              value={formData.creditCurrency}
            />
          </Box>
        )}

        <Box display="flex" gap={2}>
          <FormControl required size="medium" sx={{ flex: 1 }}>
            <InputLabel>REMIT reporting</InputLabel>
            <Select
              label="REMIT reporting"
              value={formData.remitReporting}
              onChange={(e) => handleChange("remitReporting", e.target.value)}
            >
              <MenuItem value="needed">Needed</MenuItem>
              <MenuItem value="not_needed">Not needed</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={complianceAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              complianceAssignees.data?.find(
                (a) => a.email === formData.remitReportingAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("remitReportingAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="REMIT reporting assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {formData.remitReporting === "needed" && (
          <FormControl required size="medium">
            <InputLabel>Reporting setup</InputLabel>
            <Select
              label="Reporting setup"
              value={formData.reportingSetup}
              onChange={(e) => handleChange("reportingSetup", e.target.value)}
            >
              <MenuItem value="initiated">Initiated</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="not_completed">Not completed</MenuItem>
            </Select>
          </FormControl>
        )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, signOffNote: value }))
          }
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />

        <WarehouseSyncDialog
          open={syncOpen}
          onClose={() => setSyncOpen(false)}
          catalystId={catalystId}
          entityType="broker"
          hubValues={hubSyncValues}
          onApply={handleApplySync}
        />
      </Box>
    </LifecycleDialog>
  );
};
